import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment as env } from 'src/environments/environment';
import { LoginModel } from 'src/app/models/login';
import { JWTUser } from 'src/app/models/jwtuser';
import { firstValueFrom, lastValueFrom, Observable } from 'rxjs';
import { th } from 'date-fns/locale';
import { CookieService } from 'ngx-cookie-service';

@Injectable( {
  providedIn: 'root'
} )

export class AuthService {
  readonly tokenLocalStorageKey = env.userTokenStore;

  private _requestHeaders: HttpHeaders = new HttpHeaders( { "content-type": "application/json", "accept": "application/json" } );
  private _user: SessionUser;

  constructor ( private http: HttpClient, private cookieService: CookieService, ) { }

  get token () {
    let token: string;
    if ( this.user?.token ) {
      token = this.user.token;
    }
    token = this.getCookie( env.userTokenStore );
    return token;
  }

  get requestHeaders (): HttpHeaders {
    return this._requestHeaders;
  }

  private set requestHeaders ( requestHeaders: HttpHeaders ) {
    this._requestHeaders = requestHeaders;
  }

  get user () {
    if ( !this._user ) {
      let res = JSON.parse( sessionStorage.getItem( '_user' ) );
      this._user = new SessionUser(res?.token, res?.company, res?.fullname, res?.username, res?.forceReset, res?.timeZone, res?.timezoneOffset);
    }
    return this._user;
  }

  private set user ( user: SessionUser ) {
    this._user = user;
  }

  async loginUser ( username: string, password: string ): Promise<any> {
    try {
      let data: LoginModel = { Username: username, Password: password };
      const res = await  this.http.post( `${ env.apiURL }auth/login`, data ).toPromise() as any;
      
      this.setLoginUser(res);
      return res;
    }
    catch ( err ) {
      throw err;
    }
  }

  setLoginUser(res: any) {
    this.user = new SessionUser(res.token, res.company, res.fullname, res.username, res.forceReset, res.timeZone, res.timezoneOffset);
    this.setCookie(env.userTokenStore, res.token);
    this.setCookie(env.clientInformationStore, JSON.stringify(res.clientInfo));
  }

  logoutUser () {
    this.user = null;// new SessionUser('','','','');
    const columnWidths = sessionStorage.getItem(`columnWidths`);
    sessionStorage.clear();
    this.cookieService.deleteAll();
    localStorage.clear();
    sessionStorage.setItem(`columnWidths`, columnWidths);

  }

  sendResetPasswordMail ( data: LoginModel ): Promise<any> {
    return lastValueFrom( this.http.post( env.apiURL + `auth/password/reset`, data ) );
  };

  async resetPassword ( password: string, passwordToken: string, isExternalToken: boolean ): Promise<any> {
    let data: LoginModel = { Username: "", Password: password };
    if ( isExternalToken && passwordToken ) {
      this.user = new SessionUser( passwordToken, "", "", "" ,false, "",0 );
      this.setCookie( env.userTokenStore, passwordToken );
    };
    const pasUrl = `${ env.apiURL }auth/password/change`;
    const result = await lastValueFrom( this.http.post( pasUrl, data ) );
    return result;
  }

  setCookie ( cname: string, cvalue: string ) {
    document.cookie = `${ cname }=${ cvalue };`;
  }

  getCookie ( cname: string ): string {
    let name = cname + "=";
    let ca = document.cookie.split( ';' );
    for ( let i = 0; i < ca.length; i++ ) {
      let c = ca[ i ];
      while ( c.charAt( 0 ) == ' ' ) {
        c = c.substring( 1 );
      }
      if ( c.indexOf( name ) == 0 ) {
        return c.substring( name.length, c.length );
      }
    }
    return null;
  }

  async updateClient ( data: any ) {
    let header = new HttpHeaders( {
      'Content-Disposition': 'multipart/form-data'
    } );
    return await lastValueFrom( this.http.post( `${ env.apiURL }auth/updateclient`, data, { headers: header } ) );
  }

  async sendCode ( data: any ) {
    return await lastValueFrom( this.http.post( `${ env.apiURL }auth/sendcode`, data ) );
  }

  async verifyEmail ( data:any ) {
    return await lastValueFrom( this.http.post( `${ env.apiURL }auth/verifyemail`, data ) );
  }

  async saveClient ( data: any ) {
    return await lastValueFrom( this.http.post( `${ env.apiURL }auth/saveclient`, data ) );
  }

  updatePassword ( data: any ): Promise<any> {
    return lastValueFrom( this.http.post( env.apiURL + `auth/updatepassword`, data ) );
  };

  setPermissions(data: any) {
    localStorage.setItem(env.opPermissionStore, JSON.stringify(data)); // Store in local storage
  }

  getPermissions(): any {
    return JSON.parse(localStorage.getItem(env.opPermissionStore) || '{}');
  }

  hasPermission(permissionKey: string): boolean {
    return !!this.getPermissions()[permissionKey];
  }
}

export class SessionUser {
  readonly token: string;
  readonly company: string;
  readonly fullName: string;
  readonly userName: string;
  readonly forceReset: boolean;
  readonly timeZone: string;
  readonly timezoneOffset: number;

  constructor(token: string, company: string, fullName: string, userName: string, forceReset: boolean, timeZone: string, timezoneOffset: number) {
    this.token = token;
    this.company = company;
    this.fullName = fullName;
    this.userName = userName;
    this.forceReset = forceReset;
    this.timeZone = timeZone;
    this.timezoneOffset = timezoneOffset;
  }
}
